import { useMemo } from 'react'
import { useLocation } from 'react-router'

import BankOutlinedIcon from '@ant-design/icons/BankOutlined'
import BarChartOutlinedIcon from '@ant-design/icons/BarChartOutlined'
import CalendarOutlinedIcon from '@ant-design/icons/CalendarOutlined'
import ClockCircleOutlinedIcon from '@ant-design/icons/ClockCircleOutlined'
import CommentOutlinedIcon from '@ant-design/icons/CommentOutlined'
import DashboardOutlinedIcon from '@ant-design/icons/DashboardOutlined'
import ExperimentOutlinedIcon from '@ant-design/icons/ExperimentOutlined'
import FieldTimeOutlinedIcon from '@ant-design/icons/FieldTimeOutlined'
import FileSearchOutlinedIcon from '@ant-design/icons/FileSearchOutlined'
import FlagOutlinedIcon from '@ant-design/icons/FlagOutlined'
import LineChartOutlinedIcon from '@ant-design/icons/LineChartOutlined'
import NotificationOutlinedIcon from '@ant-design/icons/NotificationOutlined'
import PrinterOutlinedIcon from '@ant-design/icons/PrinterOutlined'
import ProjectOutlinedIcon from '@ant-design/icons/ProjectOutlined'
import SettingOutlinedIcon from '@ant-design/icons/SettingOutlined'
import SolutionOutlinedIcon from '@ant-design/icons/SolutionOutlined'
import TeamOutlinedIcon from '@ant-design/icons/TeamOutlined'
import UserAddOutlinedIcon from '@ant-design/icons/UserAddOutlined'
import UsergroupAddOutlinedIcon from '@ant-design/icons/UsergroupAddOutlined'
import GuardedIcon from '@icons/GuardedIcon'
import MedicalInformationIcon from '@icons/MedicalInformationIcon'
import PillIcon from '@icons/PillIcon'

import Box from '@mui/material/Box'
import List from '@mui/material/List'
import Typography from '@components/_mui/Typography'

import useIndicator, { Indicator } from '@shared/hooks/src/useIndicator'
import useUnreadConversationsCount from '@shared/hooks/src/useUnreadConversationsCount'
import useUserSetting from '@shared/hooks/src/useUserSetting'
import { isUserAllowed, RoleGuard, useMe } from '@shared/providers/src/MeProvider'
import { UserRole, UserSettingName } from '@shared/utils'

import { useLayoutState } from '@providers/LayoutStateProvider'
import usePendingHealthHistoryAlerts from '@hooks/usePendingHealthHistoryAlerts'
import useUnreviewedLabs from '@hooks/useUnreviewedLabs'
import { rubyLink } from '@config'

import NavItem from './NavItem'

export default function Menu() {
  const me = useMe()
  const { pathname } = useLocation()

  const accessRights = useUserSetting(me, UserSettingName.AccessRights)
  const hasAccessToBlackoutPeriods = accessRights?.value?.blackout_periods === true

  const { data: hasAppointmentsPending } = useIndicator(
    Indicator.HasOnDemandOrAsyncMessaging,
    { scope: 'accessible' },
    {
      select: (data) => data.show_icon,
      enabled: isUserAllowed(me, [UserRole.Provider, UserRole.Admin]),
    }
  )

  const pendingHealthHistory = usePendingHealthHistoryAlerts({
    enabled: isUserAllowed(me, [UserRole.Provider]) || me.superAdmin,
  })

  const unreadConversationsCount = useUnreadConversationsCount()
  const unreviewedLabs = useUnreviewedLabs({ enabled: true })

  const isProvider = me.role === UserRole.Provider

  const sidebarItemList = useMemo(() => {
    return {
      dashboard: <NavItem title="Dashboard" icon={DashboardOutlinedIcon} selected={pathname === '/app'} url="/app" />,
      scorecard: <NavItem title="Scorecard" icon={BarChartOutlinedIcon} selected={pathname === '/app/scorecard'} url="/app/scorecard" />,
      kanban: <NavItem title="Task Dashboards" icon={ProjectOutlinedIcon} selected={pathname === '/app/kanban'} url="/app/kanban" />,
      escalations: (
        <NavItem title="Escalations" icon={FlagOutlinedIcon} selected={pathname === '/app/escalations'} url="/app/escalations" />
      ),
      patients: <NavItem title="Patients" icon={UserAddOutlinedIcon} selected={pathname === '/app/patients'} url="/app/patients" />,
      messages: (
        <NavItem
          title="Messages"
          icon={CommentOutlinedIcon}
          action={<Notification color="warning" count={unreadConversationsCount} />}
          url="/app/conversations"
          selected={pathname.startsWith('/app/conversations')}
        />
      ),
      appointments: (
        <NavItem
          title="Appointments"
          icon={CalendarOutlinedIcon}
          url={`/app/appointments?status=Scheduled_Waiting${isProvider ? `&provider=${me.provider.id}` : ''}`}
          selected={pathname === '/app/appointments'}
          action={<Notification color="error" variant="light" count={hasAppointmentsPending ? '!' : null} />}
        />
      ),
      hhAlerts: (
        <NavItem
          title="HH Alerts"
          icon={NotificationOutlinedIcon}
          action={<Notification color="warning" count={pendingHealthHistory} />}
          url={`/app/hh-alerts${isProvider ? `?provider=${me.provider.id}` : ''}`}
          selected={pathname === '/app/hh-alerts'}
        />
      ),
      labs: (
        <NavItem
          title="Labs"
          icon={ExperimentOutlinedIcon}
          action={<Notification color="warning" count={unreviewedLabs} />}
          url={`/app/labs?status=Needs%20Review_Released_Nurse%20Reviewed_Needs%20Follow-up${isProvider ? `&patientProvider=${me.provider.id}` : ''}`}
          selected={pathname === '/app/labs'}
        />
      ),
      prescriptions: (
        <NavItem title="Prescriptions" icon={PillIcon} url="/app/prescriptions" selected={pathname === '/app/prescriptions'} />
      ),
      assessments: (
        <NavItem
          title="Health Assessments"
          icon={MedicalInformationIcon}
          url={`/app/assessments${isProvider ? `?provider=${me.provider.id}&statuses=finished` : ''}`}
          selected={pathname === '/app/assessments'}
          action={<Notification color="warning" count={me.pendingAssessments} />}
        />
      ),
      quarterlyFlows: (
        <NavItem
          key="quarterlyFlows"
          title="Q Flow Labs"
          icon={FieldTimeOutlinedIcon}
          url={`/app/lab-requests?statuses=pending-in_progress-error${isProvider ? `&provider=${me.provider.id}` : ''}`}
          selected={pathname === '/app/lab-requests'}
          action={<Notification color="warning" count={me.pendingFlows} />}
        />
      ),
      notificationLog: (
        <NavItem
          title="Notification Log"
          icon={NotificationOutlinedIcon}
          selected={pathname === '/app/notifications'}
          url="/app/notifications"
        />
      ),
      reportLog: (
        <NavItem title="Report Log" icon={FileSearchOutlinedIcon} selected={pathname === '/app/report-logs'} url="/app/report-logs" />
      ),
      dohFaxNumbers: (
        <NavItem
          title="DOH Fax Numbers"
          icon={PrinterOutlinedIcon}
          selected={pathname === '/app/doh-fax-numbers'}
          url="/app/doh-fax-numbers"
        />
      ),
      scheduling: (
        <NavItem
          title="Scheduling"
          icon={ClockCircleOutlinedIcon}
          url="/app/scheduling"
          selected={pathname === '/app/scheduling'}
          action={<Notification color="error" variant="light" count={me.hasPendingAvailabilityApprovals ? '!' : null} />}
        />
      ),
      reports: <NavItem title="Reports" icon={LineChartOutlinedIcon} url="/app/reports" selected={pathname === '/app/reports'} />,
      providers: (
        <NavItem title="Providers" icon={UsergroupAddOutlinedIcon} url="/app/providers" selected={pathname.startsWith('/app/providers')} />
      ),
      pharmacies: (
        <NavItem title="Pharmacies" icon={BankOutlinedIcon} url="/app/pharmacies" selected={pathname.startsWith('/app/pharmacies')} />
      ),
      cbos: <NavItem title="CBOs" icon={SolutionOutlinedIcon} url="/app/cbos" selected={pathname.startsWith('/app/cbos')} />,
      users: <NavItem title="System Users" icon={TeamOutlinedIcon} url="/app/users" selected={pathname.startsWith('/app/users')} />,
      platformSettings: (
        <NavItem
          title="Platform Settings"
          icon={SettingOutlinedIcon}
          url="/app/admin/platform-settings"
          selected={pathname === '/app/admin/platform-settings'}
        />
      ),
      superAdmin: <NavItem title="Super Admin" icon={GuardedIcon} url={rubyLink('super_admin_actions')} />,
    }
  }, [me, pathname, hasAppointmentsPending, unreadConversationsCount, pendingHealthHistory, unreviewedLabs, isProvider])

  return (
    <Box sx={styles.scroll}>
      <Box sx={styles.container}>
        <List disablePadding>
          {sidebarItemList.dashboard}

          <RoleGuard allowed={[UserRole.Provider]}>
            {sidebarItemList.escalations}
            {sidebarItemList.scorecard}
            {sidebarItemList.patients}
            {sidebarItemList.messages}
            {sidebarItemList.appointments}
            {sidebarItemList.labs}
            {sidebarItemList.prescriptions}
            {sidebarItemList.assessments}
            {sidebarItemList.hhAlerts}
            {sidebarItemList.scheduling}
            {sidebarItemList.pharmacies}
            {sidebarItemList.reports}
            {sidebarItemList.quarterlyFlows}
          </RoleGuard>

          <RoleGuard allowed={[UserRole.MA]}>
            {sidebarItemList.escalations}
            {sidebarItemList.scorecard}
            {sidebarItemList.patients}
            {sidebarItemList.messages}
            {sidebarItemList.appointments}
            {sidebarItemList.labs}
            {sidebarItemList.prescriptions}
            {sidebarItemList.scheduling}
            {sidebarItemList.pharmacies}
            {sidebarItemList.users}
            {sidebarItemList.reports}
            {sidebarItemList.dohFaxNumbers}
          </RoleGuard>

          <RoleGuard allowed={[UserRole.Support]}>
            {sidebarItemList.escalations}
            {sidebarItemList.scorecard}
            {sidebarItemList.patients}
            {sidebarItemList.messages}
            {sidebarItemList.appointments}
            {sidebarItemList.labs}
            {sidebarItemList.prescriptions}
            {sidebarItemList.scheduling}
            {sidebarItemList.pharmacies}
            {sidebarItemList.reports}
            <SectionHeader>Other</SectionHeader>
            {sidebarItemList.notificationLog}
            {sidebarItemList.dohFaxNumbers}
          </RoleGuard>

          <RoleGuard allowed={[UserRole.Admin]}>
            {sidebarItemList.kanban}
            {sidebarItemList.patients}
            {sidebarItemList.messages}
            {sidebarItemList.appointments}
            {sidebarItemList.labs}
            {sidebarItemList.prescriptions}
            {sidebarItemList.assessments}
            {me.superAdmin && sidebarItemList.hhAlerts}
            {sidebarItemList.scheduling}
            {sidebarItemList.reports}

            <SectionHeader>Administration</SectionHeader>
            {sidebarItemList.pharmacies}
            {sidebarItemList.cbos}
            {sidebarItemList.users}
            {sidebarItemList.notificationLog}
            {sidebarItemList.reportLog}
            {sidebarItemList.dohFaxNumbers}
            {(me.superAdmin || hasAccessToBlackoutPeriods) && sidebarItemList.platformSettings}
            {me.superAdmin && sidebarItemList.superAdmin}
          </RoleGuard>

          <RoleGuard allowed={[UserRole.CBO]}>
            {sidebarItemList.escalations}
            {sidebarItemList.patients}
            {sidebarItemList.messages}
            {sidebarItemList.appointments}
            {sidebarItemList.labs}
            {sidebarItemList.prescriptions}
            {sidebarItemList.reports}
          </RoleGuard>

          <RoleGuard allowed={[UserRole.Pharmacy, UserRole.PharmacyManager]}>
            {sidebarItemList.patients}
            {sidebarItemList.messages}
            {sidebarItemList.labs}
            {sidebarItemList.prescriptions}
            {sidebarItemList.reports}
          </RoleGuard>

          <RoleGuard allowed={[UserRole.Manager, UserRole.Manager340B]}>
            {sidebarItemList.patients}
            {sidebarItemList.messages}
            {sidebarItemList.appointments}
            {sidebarItemList.labs}
            {sidebarItemList.prescriptions}
            {sidebarItemList.scheduling}
            {sidebarItemList.pharmacies}
            {sidebarItemList.reports}
          </RoleGuard>

          <RoleGuard allowed={[UserRole.Compliance]}>
            {sidebarItemList.patients}
            {sidebarItemList.appointments}
            {sidebarItemList.labs}
            {sidebarItemList.prescriptions}
            {sidebarItemList.pharmacies}
          </RoleGuard>

          <RoleGuard allowed={[UserRole.ClinicalAdmin]}>
            {sidebarItemList.patients}
            {sidebarItemList.messages}
            {sidebarItemList.appointments}
            {sidebarItemList.labs}
            {sidebarItemList.prescriptions}
            {sidebarItemList.scheduling}
            {sidebarItemList.pharmacies}
            {sidebarItemList.reports}
          </RoleGuard>

          <RoleGuard allowed={[UserRole.ClinicalLHC, UserRole.ClinicalQI]}>
            {sidebarItemList.patients}
            {sidebarItemList.messages}
            {sidebarItemList.appointments}
            {sidebarItemList.labs}
            {sidebarItemList.prescriptions}
            {sidebarItemList.scheduling}
            {sidebarItemList.pharmacies}
            {sidebarItemList.reports}
          </RoleGuard>
        </List>
      </Box>
      <Typography sx={styles.copyright}>© {new Date().getFullYear()} QCare Plus</Typography>
    </Box>
  )
}

function SectionHeader({ children }) {
  const { sidebarCollapsed } = useLayoutState()
  if (sidebarCollapsed) return <Box sx={styles.sectionSpacer} />
  return (
    <Typography variant="body2" sx={styles.sectionHeader}>
      {children}
    </Typography>
  )
}

function Notification({ count = 0, color = 'warning' }) {
  const { sidebarCollapsed: mini } = useLayoutState()
  if (!count) return null
  if (mini || count === '!') {
    return <Box sx={{ ...styles.dot, bgcolor: `${color}.main`, boxShadow: (theme) => `0 0 0 3px ${theme.palette[color].main}26` }} />
  }
  const label = typeof count === 'number' && count > 99 ? '99+' : count
  return <Box sx={styles.pill}>{label}</Box>
}

const styles = {
  scroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    py: 1,
  },
  copyright: {
    p: 1,
    color: 'text.disabled',
    mt: 'auto',
  },
  sectionHeader: {
    fontWeight: '600',
    pl: 3,
    pt: 2,
  },
  sectionSpacer: {
    pt: 1,
  },
  pill: {
    minWidth: 26,
    height: 22,
    px: 1,
    borderRadius: 11,
    fontSize: '0.8rem',
    fontWeight: 600,
    bgcolor: 'action.selected',
    color: 'text.primary',
    display: 'grid',
    placeItems: 'center',
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
  },
}
